<template>
  <div class="opc-cart__total">
    <div class="opc-cart__total__sub" v-if="showSubTotal">
      <div class="opc-cart__total__text">{{ $t('billing.order_summary.subtotal') }}</div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ subTotalAmountText }}
      </div>
    </div>
    <div class="opc-cart__total__discount" v-if="discount">
      <div class="opc-cart__total__text">{{ $t('billing.coupon.discount') }}</div>
      <div class="opc-cart__total__text text-no-capitalize">{{ this.$money(this.discount) }}</div>
    </div>
    <div class="opc-cart__total__tax" v-if="isShowTax && isTaxEstimatedBefore">
      <div class="opc-cart__total__text __PLACEHOLDER_total__tax">{{ $t('billing.estimated_tax') }}</div>
      <div class="opc-cart__total__text text-no-capitalize">
        <i v-if="isLoadingTax || isLoadingPreview" class="zm-icon-loading" />
        <span v-else-if="$context.is.delta">
          {{ this.$money(this.getProratedChargeTaxAmount) }}
        </span>
        <span v-else>
          {{ this.$money(this.getTaxTotalAmount) }}
        </span>
      </div>
    </div>
    <inr-discount/>
    <div class="opc-cart__total__cba" v-if="showCreditBalance">
      <div class="opc-cart__total__text">{{ $t('billing.credit_balance') }}</div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.$money(this.getCreditBalance) }}
      </div>
    </div>
    <div class="opc-cart__total__main" v-if="showProratedCharge">
      <div class="opc-cart__total__text">
        <total-label />
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.$money(this.getProratedChargeTotalAmount) }}
      </div>
    </div>
    <div class="opc-cart__main__divider" v-if="showProratedCharge"></div>
    <div class="opc-cart__total__main" v-if="showTotal">
      <div class="opc-cart__total__text">
        <total-label />
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.$money(includeTaxTotalAmount) }}
        <p class="opc-cart__total__text__sub text-no-capitalize" v-if="showPlusTaxTxt">
          <priceVATTip />
          <template v-if="!showVATTip">{{ $t('billing.order_summary.plus_applicable_taxes') }}</template>
        </p>
      </div>
    </div>
    <next-charge v-else-if="!isPureFreeTrialFlow" />
    <!-- // FREE TRIAL - specific total charge display for free trial -->
    <div class="opc-cart__total__main" v-if="isPureFreeTrialFlow">
      <div class="opc-cart__total__text">
        <total-label />
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.$money(0) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import _ from 'lodash';
import NextCharge from './next.vue';
import TotalLabel from './total-label.vue';
import flow from '@/flows';
import { pageConfig } from '@/common/service/page-config';
import { planTypes, VIEWS, ALLOW_MIXED_BUY_FREE_TRIAL_PLANS } from '@/common/constants';
import { plansof } from '@/utils/chain';
import InrDiscount from "./inr-discount";

export default {
  components: {
    TotalLabel,
    NextCharge,
    InrDiscount
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'getSelectedPlansAmount',
      'getTotalCharge',
      'taxPayload',
      'getTaxTotalAmount',
      'isTaxExempt',
      'isTaxEstimatedBefore',
      'isLoadingTax',
      'discountWithAmountMap',
      'soldToContact',
      'selectedPlanListPayLoad',
      'addressInfo',
      'isInrCitEligible',
      'isProratedChargeAmountCalculated',
      'getProratedChargeTotalAmount',
      'getProratedChargeTaxAmount',
      'isLoadingPreview',
      'getCreditBalance',
      'openInvoiceTotalAmount',
      'includeTaxTotalAmount',
    ]),
    isFreeTrialFlow () {
      return this.$context.is.add2trial || this.$context.is.buyTrial;
    },
    // BUY TRIAL
    // FREE TRIAL - specific total charge display for free trial
    isPureFreeTrialFlow() {
      return this.isFreeTrialFlow && !plansof(this.getSelectedPlans).not().freeTrial().has() ;
    },
    isMixedFreeTrialFlow() {
      return this.isFreeTrialFlow &&
        plansof(this.getSelectedPlans).type(...ALLOW_MIXED_BUY_FREE_TRIAL_PLANS).has() &&
        plansof(this.getSelectedPlans).not().freeTrial().has()
    },
    discount() {
      return this.discountWithAmountMap?.totalDiscount ?? 0;
    },
    isShowTax() {
      // FREE TRIAL - won't show tax fee for free trial, except it's under FCCA compliance
      if (this.isPureFreeTrialFlow && this.addressInfo.country !== 'DE') {
        return false;
      }
      // isTaxExempt in zuora account is only part of the whole tax amount
      if (flow.view() === VIEWS.COMPOUND || this.$context.is.modal) {
        return true;
      }
      if (this.$context.is.configPage) {
        if (this.isTaxEstimatedBefore) return true;
        return false;
      }
      return true;
    },
    showSubTotal() {
      return this.getSelectedPlans?.length > 1 || this.partnerShowSubTotal;
    },
    partnerShowSubTotal() {
      return this.$context.is.add2cart
        && [planTypes.MASTER_CLASS].includes(pageConfig?.editConfig?.planType)
        && this.getSelectedPlans?.length > 0;
    },
    partnerShowTotal() {
      return this.$context.is.add2cart
        && [planTypes.MASTER_CLASS].includes(pageConfig?.editConfig?.planType);
    },
    showTotal() {
      return this.$context.is.buyNow || this.$context.is.basic2pro || this.partnerShowTotal || this.isMixedFreeTrialFlow;
    },
    showProratedCharge() {
      if (this.$context.is.delta) return true;
      return this.isInrCitEligible && this.isProratedChargeAmountCalculated;
    },
    showCreditBalance() {
      return this.isInrCitEligible && this.getCreditBalance > 0;
    },
    showPlusTaxTxt(){
      const is0total = ( this.getTotalCharge + this.getTaxTotalAmount === 0 )
      return !(this.isShowTax && this.isTaxEstimatedBefore) && !is0total
    },
    subTotalAmountText() {
      const subTotal = this.getSelectedPlansAmount.totalAmount + this.openInvoiceTotalAmount;
      return this.$money(Math.max(subTotal, 0));
    }
  },
  methods: {
    ...mapMutations(['setIsLoadingTax', 'setIsTaxUpdateAttempted']),
    ...mapActions(['estimateTax']),
    reloadTax: _.debounce(async function () {
      const picked = _.pick(this.soldToContact, ['country', 'city', 'zip']);
      if (!this.isShowTax || !this.selectedPlanListPayLoad.length || Object.values(picked).some((x) => !x)) {
        this.setIsTaxUpdateAttempted(true);
        return;
      }
      try {
        this.setIsLoadingTax(true);
        await this.estimateTax();
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.setIsTaxUpdateAttempted(true);
        this.setIsLoadingTax(false);
      }
    }, 800),
  },
  watch: {
    taxPayload: {
      deep: true,
      immediate: true,
      handler() {
        this.setIsTaxUpdateAttempted(false);
        this.reloadTax();
      },
    },
  },
};
</script>

<style lang="scss">
@import "total";
</style>
